'use client';

import InputBase from '@mui/material/InputBase';
import {styled} from '@mui/material/styles';
import {useId} from 'react';

import {fieldShellStyles} from './field-styles';
import {FieldShell} from './FieldShell';

const StyledInput = styled(InputBase, {shouldForwardProp: p => p !== 'oError'})<{oError?: boolean}>(({theme, oError}) => ({
  ...fieldShellStyles(theme, {error: oError}),
  '& .MuiInputBase-input': {
    fontSize: theme.font.size['body-2'],
    fontWeight: theme.font.weight.regular,
    lineHeight: 1.2,
    height: 'auto',
    padding: 0,
    color: theme.palette.colors.text.neutral.strong,
    '&::placeholder': {color: theme.palette.colors.text.neutral.placeholder, opacity: 1},
  },
}));

export interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: 'text' | 'email' | 'tel';
  required?: boolean;
  error?: boolean;
  helperText?: string;
  name?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  disabled?: boolean;
  maxLength?: number;
}

export function TextField({
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  required,
  error,
  helperText,
  name,
  autoComplete,
  inputMode,
  disabled,
  maxLength,
}: TextFieldProps) {
  const helperId = useId();
  return (
    <FieldShell label={label} required={required} showLabel={!value} helperId={helperId} error={error} helperText={helperText}>
      <StyledInput
        fullWidth
        oError={error}
        type={type}
        name={name}
        value={value}
        disabled={disabled}
        onChange={e => {
          onChange(e.target.value);
        }}
        onBlur={onBlur}
        inputProps={{
          'aria-label': label,
          'aria-required': required || undefined,
          'aria-invalid': error || undefined,
          'aria-describedby': helperText ? helperId : undefined,
          autoComplete,
          inputMode,
          maxLength,
        }}
      />
    </FieldShell>
  );
}

TextField.displayName = 'TextField';
